import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import {
  Plus, Mic, MicOff, Square, RefreshCw, CheckCircle,
  Clock, Upload, Zap, FileText, ChevronRight
} from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Progress } from './components/ui/progress';
import { Skeleton } from './components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = {
  created: { label: 'Created', step: 1 },
  recording: { label: 'Recording', step: 2 },
  uploaded: { label: 'Uploaded', step: 3 },
  processing: { label: 'AI Processing', step: 4 },
  completed: { label: 'Completed', step: 5 },
  failed: { label: 'Failed', step: 0 },
};

export default function TeacherRecordings() {
  const { user, token } = useApp();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ topic: '', class_id: '', subject_id: '' });

  const fetchData = useCallback(async () => {
    const tid = user?.tenant_id;
    try {
      const [s, cl, su] = await Promise.all([
        axios.get(`${API}/recording-sessions`, { params: { tenant_id: tid, teacher_id: user?.id } , headers }),
        axios.get(`${API}/classes`, { params: { tenant_id: tid } , headers }),
        axios.get(`${API}/subjects`, { params: { tenant_id: tid } , headers }),
      ]);
      setSessions(s.data);
      setClasses(cl.data);
      setSubjects(su.data);
    } catch { toast.error('Failed to load recordings'); }
    finally { setLoading(false); }
  }, [user?.id, user?.tenant_id]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` }; fetchData(); }, [fetchData]);

  // Auto-refresh processing sessions
  useEffect(() => {
    const processingExists = sessions.some(s => s.status === 'processing' || s.ai_processing_status === 'processing');
    if (!processingExists) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [sessions, fetchData]);

  const startRecording = async () => {
    if (!form.topic.trim()) { toast.error('Topic is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/recording-sessions`, {
        topic: form.topic,
        class_id: form.class_id || undefined,
        subject_id: form.subject_id || undefined,
        tenant_id: user.tenant_id
      , headers });
      toast.success('Recording session started! AI is processing...');
      setCreateOpen(false);
      setForm({ topic: '', class_id: '', subject_id: '' });
      fetchData();
    } catch { toast.error('Failed to start recording'); }
    finally { setCreating(false); }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#10b981',
      processing: '#0ea5e9',
      failed: '#ef4444',
      created: '#94a3b8',
      uploaded: '#f97316',
    };
    return colors[status] || '#94a3b8';
  };

  return (
    <DashboardShell title="Recording Sessions" breadcrumb="Teacher Dashboard">
      <div className="space-y-4 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {sessions.length} total sessions
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}
                data-testid="new-recording-button">
                <Plus className="w-4 h-4 mr-2" /> New Recording
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5" style={{ color: 'hsl(var(--primary))' }} />
                  New Recording Session
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm mb-1.5 block">Topic <span className="text-red-500">*</span></Label>
                  <Input placeholder="e.g. Photosynthesis, Quadratic Equations..."
                    value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                    data-testid="recording-topic-input" />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Class (optional)</Label>
                  <Select value={form.class_id} onValueChange={v => setForm(f => ({ ...f, class_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Subject (optional)</Label>
                  <Select value={form.subject_id} onValueChange={v => setForm(f => ({ ...f, subject_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-3 rounded-xl text-sm" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--muted-foreground))' }}>
                  <p className="font-medium mb-1" style={{ color: 'hsl(var(--foreground))' }}>What happens next?</p>
                  <ol className="space-y-1 text-xs list-decimal list-inside">
                    <li>Session is created and audio upload begins</li>
                    <li>AI transcribes and generates lesson draft</li>
                    <li>You review and publish the draft to students</li>
                  </ol>
                </div>

                <Button onClick={startRecording} disabled={creating} className="w-full"
                  style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}
                  data-testid="start-recording-submit">
                  {creating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><Mic className="w-4 h-4 mr-2" /> Start Recording Session</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sessions */}
        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        ) : sessions.length === 0 ? (
          <div className="empty-state">
            <Mic className="empty-state-icon" />
            <p className="text-sm font-medium">No recordings yet</p>
            <p className="text-xs mt-1">Start your first recording session</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map(s => {
              const cls = classes.find(c => c.id === s.class_id);
              const subj = subjects.find(sub => sub.id === s.subject_id);
              const isProcessing = s.status === 'processing' || s.ai_processing_status === 'processing';
              return (
                <div key={s.id} className="desky-card p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold">{s.topic}</p>
                        {isProcessing && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: 'hsl(199 89% 90%)', color: 'hsl(199 89% 25%)' }}>
                            <RefreshCw className="w-3 h-3 animate-spin" /> Processing
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        {cls && <span>{cls.name}</span>}
                        {subj && <><span>•</span><span>{subj.name}</span></>}
                        {s.duration && <><span>•</span><span>{Math.floor(s.duration / 60)} min</span></>}
                        <span>•</span><span>{new Date(s.created_at).toLocaleDateString()}</span>
                      </div>

                      {/* Pipeline Progress */}
                      {isProcessing && (
                        <div className="mt-3">
                          <Progress value={60} className="h-1" />
                          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>AI is generating your lesson draft...</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2 ml-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${s.status === 'completed' ? 'completed' : s.status === 'failed' ? 'failed' : 'processing'}`}>
                        {s.status}
                      </span>
                      {s.lesson_draft_id && (
                        <Button size="sm" variant="outline" className="text-xs h-7"
                          onClick={() => navigate(`/teacher/drafts/${s.lesson_draft_id}`)}
                          data-testid={`view-draft-${s.id}`}>
                          <FileText className="w-3.5 h-3.5 mr-1" /> View Draft
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
